import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { SortableRow } from "./SortableRow";
import { FieldLabel } from "../field";
import { Plus, Trash2 } from "lucide-react";

// Shared with RecordForm and RecordPasteListener -- kept here alongside
// MultiUrlInput rather than split out, at the cost of this file not being a
// clean Fast Refresh boundary.
// eslint-disable-next-line react-refresh/only-export-components
export function normalizeUrl(value: string) {
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

// eslint-disable-next-line react-refresh/only-export-components
export function checkUrlReachable(url: string): Promise<boolean> {
  // (Unused elsewhere in src/ as of this writing.)
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = `${url.replace(/\/$/, "")}/favicon.ico?${Date.now()}`;
  });
}

export function MultiUrlInput({ name }: { name: string }) {
  const { control, setValue, trigger } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name,
  });

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <FieldLabel className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground">URLs</FieldLabel>
        <Button
          size="icon"
          type="button"
          variant="secondary"
          className="h-[26px] w-[26px] rounded-[7px] bg-accent text-accent-foreground hover:bg-accent/80"
          onClick={() => append({ label: "", url: "" })}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      {fields.length > 0 && (
        <div className="overflow-y-auto max-h-48 flex flex-col gap-1">
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={(event) => {
              const { active, over } = event;
              if (!over || active.id === over.id) return;
              const oldIndex = fields.findIndex((f) => f.id === active.id);
              const newIndex = fields.findIndex((f) => f.id === over.id);
              move(oldIndex, newIndex);
            }}
          >
            <SortableContext
              items={fields.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              {fields.map((field, index) => (
                <SortableRow key={field.id} id={field.id}>
                  <div className="grid grid-cols-[2fr_1fr_auto] gap-1 items-center">
                    <Controller
                      control={control}
                      name={`${name}.${index}.url`}
                      render={({ field: fld }) => (
                        <Input
                          {...fld}
                          type="url"
                          placeholder="URL"
                          onBlur={(e) => {
                            const normalized = normalizeUrl(e.target.value);
                            setValue(`${name}.${index}.url`, normalized);
                            trigger(`${name}.${index}.url`);
                          }}
                        />
                      )}
                    />
                    <Controller
                      name={`${name}.${index}.label`}
                      control={control}
                      render={({ field }) => (
                        <Input placeholder="Label" {...field} />
                      )}
                    />
                    <Button
                      size="icon"
                      type="button"
                      variant="ghost"
                      className="text-destructive shrink-0"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
